import { ApplicationRef, createComponent, Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core'
import { WindowRef } from '@spartacus/core'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { UcService } from '../../enums/uc-service'
import { distinctUntilChanged, filter, map } from 'rxjs/operators'
import { UcElementBlockedEvent } from '../../components/shared/events/uc-element-blocked-event'
import { MbEventListenerService } from '../mb-event-listener/event-listener.service'
import { UcBlockedContentReplacerComponent } from '../../components/uc-blocked-content-replacer/uc-blocked-content-replacer.component'
import { UcEvent, UCServiceBaseInfo, UserCentrics } from '../../components/types'
import { UcBlockableElementInitializedEvent } from '../../components/shared/events/uc-blockable-element-initialized-event'
import { MbConstants } from '../../../shared/mb-constants'

@Injectable({ providedIn: 'root' })
export class UcManagementService implements OnDestroy {

  private subscription = new Subscription()
  private renderer: Renderer2 = this.rendererFactory.createRenderer(null, null)
  private blockedElementsMap: UcBlockedElementsMap = {} as UcBlockedElementsMap

  private ucServiceConfigMap = new BehaviorSubject<UcServiceConfigMap>({
    initialized: false,
    [UcService.MOVING_IMAGE]: {
      isBlocked: false,
      urls: ['asset-out-cdn.video-cdn.net', 'vod.video-cdn.net', 'd.video-cdn.net', 'api.video-cdn.net', '/v1/vms/556'],
      id: ''
    },
    [UcService.FRESH_CHAT]: {
      isBlocked: false,
      urls: ['cdn.euc-freshbots.ai', 'euc-freshbots.ai'],
      id: ''
    }
  })

  constructor (protected winRef: WindowRef,
               private eventListenerService: MbEventListenerService,
               private rendererFactory: RendererFactory2,
               private appRef: ApplicationRef) {
  }

  init () {
    const window = this.winRef?.document?.defaultView as any
    if (!window) {
      return
    }

    window.addEventListener('UC_UI_INITIALIZED', () => this.initializeMaps())
    window.addEventListener('ucEvent', (event: UcEvent) => this.handlePermissionChange(event))

    this.subscription.add(this.eventListenerService.retrieve()
      .pipe(
        filter(event => this.isBlockEvent(event.event)),
        map(event => event.event as UcElementBlockedEvent))
      .subscribe(event => this.replaceElementWithContentReplacer(event.ucId, event.ucService as UcService)))
  }

  getServiceId (service: UcService): string {
    return this.ucServiceConfigMap.value[service]?.id ?? ''
  }

  ngOnDestroy () {
    this.subscription.unsubscribe()
  }

  isBlockEvent (event: Event): boolean {
    return event instanceof UcElementBlockedEvent
      || event instanceof UcBlockableElementInitializedEvent
      && (this.ucServiceConfigMap.value[event.ucService as UcService]?.isBlocked ?? false)
  }

  initializeMaps () {
    const window = this.winRef?.document?.defaultView as any
    if (!window) {
      return
    }
    window.UC_UI.getServicesBaseInfo()
      .filter((data: UCServiceBaseInfo) => !!this.ucServiceConfigMap.value[data?.name as UcService])
      .forEach((data: UCServiceBaseInfo) => {
        const isBlocked = !data.consent.status
        this.updateServiceConfig(data.name as UcService, isBlocked, data.id)
        this.blockedElementsMap[data.name] = []
        if (isBlocked) {
          this.blockElementsByService(data.name as UcService)
        }
      })
  }

  handlePermissionChange (event: UcEvent) {
    if (!event?.detail) {
      return
    }
    Object.keys(event.detail)
      .map(service => service as UcService)
      .filter(service => !!this.ucServiceConfigMap.value[service])
      .forEach(service => {
        const isBlocked = !(event as any).detail[service]
        this.updateServiceConfig(service as UcService, isBlocked)
        return isBlocked ? this.blockElementsByService(service) : this.unblockElementsByService(service)
      })
  }

  updateServiceConfig (serviceName: UcService, isBlocked?: boolean, id?: string): void {
    const configMap = this.ucServiceConfigMap.value
    const service = configMap[serviceName] ?? {} as UcServiceConfig
    if (isBlocked !== undefined) {
      service.isBlocked = isBlocked
    }
    if (id !== undefined) {
      service.id = id
    }
    configMap[serviceName] = service
    configMap.initialized = true
    this.ucServiceConfigMap.next(configMap)
  }

  blockElementsByService (service: UcService): void {
    if (!this.winRef.isBrowser()) {
      return
    }
    Array.from(this.winRef.document.querySelectorAll(`[data-uc-service="${service}"]`))
      .map(element => element.getAttribute('data-uc-id') ?? '')
      .filter(ucId => !!ucId)
      .forEach(ucId => this.replaceElementWithContentReplacer(ucId, service))
  }

  unblockElementsByService (service: UcService): void {
    if (!this.winRef.isBrowser()) {
      return
    }
    if (!this.blockedElementsMap[service]?.length) {
      this.blockedElementsMap[service] = []
      return
    }
    this.blockedElementsMap[service].forEach(blockedElement => {
      this.renderer.removeClass(blockedElement.originalElement, MbConstants.UC_SERVICE_BLOCK_LINK_CLASS)
      this.renderer.removeClass(blockedElement.originalElement, MbConstants.UC_SERVICE_BLOCK_CLASS)
      this.renderer.removeChild(blockedElement.replacedElement.parentNode, blockedElement.replacedElement)
    })
    this.blockedElementsMap[service] = []
  }

  replaceElementWithContentReplacer (ucId: string, ucService: UcService): void {
    const elements = Array.from(this.winRef.document.querySelectorAll(`[data-uc-id="${ucId}"]`))

    elements.forEach((element) => {
      const classList = element.classList
      const useBasicThumbnail = JSON.parse(element.getAttribute('data-use-basic-thumbnail') ?? 'false')
      if (classList.contains(MbConstants.UC_SERVICE_BLOCK_CLASS) || classList.contains(MbConstants.UC_SERVICE_BLOCK_LINK_CLASS)) {
        return
      }

      const componentRef = createComponent(UcBlockedContentReplacerComponent, { environmentInjector: this.appRef.injector })
      componentRef.instance.ucId = ucId
      componentRef.instance.ucService = ucService
      componentRef.instance.ucServiceId = this.ucServiceConfigMap.value[ucService]?.id ?? ''
      componentRef.instance.useBasicThumbnail = useBasicThumbnail
      componentRef.changeDetectorRef.detectChanges()

      if (element.classList.contains('related-item-link') || element.classList.contains('related-link')) {
        this.renderer.addClass(element, MbConstants.UC_SERVICE_BLOCK_LINK_CLASS)
        this.addBlockedElement(element, componentRef.location.nativeElement, ucService)
      } else {
        this.addBlockedElement(element, componentRef.location.nativeElement, ucService)
        this.renderer.insertBefore(element.parentNode, componentRef.location.nativeElement, element)
        this.renderer.addClass(element, MbConstants.UC_SERVICE_BLOCK_CLASS)
      }
    })
  }

  shouldRequestBeBlocked (service: UcService, requestUrl: string): Observable<boolean> {
    return this.ucServiceConfigMap$.pipe(
      map(configMap => configMap[service] ?? {} as UcServiceConfig),
      map(serviceConfig => serviceConfig.isBlocked && !!serviceConfig.urls?.some(url => requestUrl.includes(url))))
  }

  addBlockedElement (originalElement: any, replacedElement: any, ucService: string): void {
    if (!this.blockedElementsMap[ucService]) {
      this.blockedElementsMap[ucService] = []
    }
    this.blockedElementsMap[ucService].push({ originalElement, replacedElement })
  }

  acceptService (serviceId: string | undefined): void {
    const window = this.winRef?.document?.defaultView
    if (!this.winRef.isBrowser() || !serviceId || !window) {
      return
    }
    const userCentrics = window.UC_UI as UserCentrics
    userCentrics.acceptService(serviceId)
  }

  openUCServiceInfo (serviceId: string | undefined): void {
    const window = this.winRef?.document?.defaultView as any
    if (!window) {
      return
    }
    const userCentrics = window.UC_UI as UserCentrics
    userCentrics.showSecondLayer(serviceId)
  }

  get ucServiceConfigMap$ (): Observable<UcServiceConfigMap> {
    return this.ucServiceConfigMap.pipe(
      filter(conf => conf.initialized),
      map(conf => {
        const filteredEntries: UcServiceConfigMap = { initialized: conf.initialized }
        Object.entries(conf)
          .filter(([_, value]) => (value as UcServiceConfig)?.id?.length > 0)
          .forEach(([key, value]) => filteredEntries[key as UcService] = value as UcServiceConfig)
        return filteredEntries
      }))
  }

  isServiceBlocked (service: UcService): boolean {
    return this.ucServiceConfigMap.value[service]?.isBlocked ?? false
  }

  removeBlockedContentReplacerFromDOM (videoId: string, cssClassName: string) {
    const elements = Array.from(this.winRef.document.querySelectorAll(`[data-replacer-uc-id="${videoId}"]`))
    const elementsToRemove = elements?.filter(element => element?.parentElement?.parentElement?.classList?.contains(cssClassName))
    elementsToRemove?.forEach(element => {
      element?.parentElement?.remove()
    })
  }

  isServiceBlocked$ (service: UcService): Observable<boolean> {
    return this.ucServiceConfigMap$.pipe(
      filter(configMap => configMap.initialized),
      map(configMap => configMap[service]),
      map(serviceConfig => serviceConfig?.isBlocked ?? false),
      distinctUntilChanged())
  }
}

export type UcBlockedElementsMap = Record<string, BlockedElementData[]>

export interface UcServiceConfig {
  isBlocked: boolean;
  urls: string[];
  id: string;
}

export interface UcServiceConfig {
  isBlocked: boolean;
  urls: string[];
  id: string;
}

export type UcServiceConfigMap = {
  initialized: boolean;
} & {
  [key in UcService]?: UcServiceConfig;
}

export interface BlockedElementData {
  originalElement: HTMLElement;
  replacedElement: HTMLElement;
}
